import React, { FC } from 'react';
import { MailOutline } from '@mui/icons-material';
import { Box, Button, Typography } from 'drift-ui';
import Icon, { ImageName } from './Icon';
import { CurrentUser, getPersmissionsLevel } from '../utilities/authUtils';
import { CARRIER_COMPLIANCE_EMAIL } from '../constants/constants';
import { CompanyPermissions } from '../features/findLoads/permissionsType';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../configureStore';
import { openPermissionDialog } from '../features/findLoads/permissionsState';

interface SplashPageProps {
  currentUser: CurrentUser;
  persmissions: CompanyPermissions;
  type: 'Compliance' | 'Permission level' | 'Access Requested';
}

const containerStyles = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  height: '80vh',
  width: '90%',
  margin: '120px auto',
  borderRadius: '10px',
  boxShadow: '0 2px 14px -1px rgba(0, 0, 0, 0.25)',
};

const infoContainerStyles = {
  marginTop: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  alignItems: 'center',
};

const SplashPage: FC<SplashPageProps> = ({
  currentUser,
  persmissions,
  type,
}) => {
  const dispatch = useDispatch();

  const { dialogOpen } = useSelector(
    (state: RootState) => state.permissionDialog
  );

  let cardTitle: string = '';
  let cardText: string = '';
  let permissionLevel: string = '';
  let buttonText: string = '';
  let iconName: ImageName = 'FreightAlert';

  if (type === 'Access Requested') {
    cardTitle = 'Load board access requested.';
    permissionLevel = getPersmissionsLevel(persmissions.companyPermissions);
    cardText =
      'Waiting on your company admin to approve your request. Reach out to them directly for updates.';
    buttonText = 'Resubmit request';
    iconName = 'Glass';
  } else if (type === 'Permission level') {
    cardTitle = 'Need access to the load board?';
    permissionLevel = getPersmissionsLevel(persmissions.companyPermissions);
    cardText = `Your company qualifies for ${permissionLevel}. \nContact your administrator to enable this for you.`;
    buttonText = 'Request access';
    iconName = 'FreightBox';
  } else {
    cardTitle = 'You do not currently qualify for load board access.';
    cardText = 'Please contact us to verify your eligibility.';
    buttonText = 'Contact us';
  }

  const handleOnClick = () => {
    if (type === 'Permission level' || type === 'Access Requested') {
      dispatch(openPermissionDialog());
    } else {
      globalThis.location.href = `mailto:${CARRIER_COMPLIANCE_EMAIL}?subject=Load Board Access Request ${currentUser.mcleod_carrier_id}`;
    }
  };

  return !dialogOpen ? (
    <Box sx={containerStyles}>
      <Icon name={iconName} alt="Freight Alert Box" />
      <Box sx={infoContainerStyles}>
        <Typography
          variant="h3"
          fontWeight={600}
          fontSize={24}
          lineHeight="29px"
        >
          {cardTitle}
        </Typography>

        <Typography
          fontWeight={type === 'Permission level' ? 400 : 500}
          fontSize={type === 'Permission level' ? 16 : 18}
          lineHeight={'24px'}
          color="#666666"
          sx={{ whiteSpace: 'pre-line' }}
        >
          {cardText}
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={type === 'Compliance' && <MailOutline />}
          onClick={handleOnClick}
        >
          {buttonText}
        </Button>
      </Box>
    </Box>
  ) : null;
};

export default SplashPage;
